import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './Services.css';
import {
  FaCode, FaMobileAlt, FaBrain, FaCloud, FaPaintBrush, FaServer,
  FaReact, FaPython, FaNodeJs, FaAws, FaDocker
} from 'react-icons/fa';
import { SiDjango, SiMongodb } from 'react-icons/si';
import AnimatedOnScroll from '../components/AnimatedOnScroll'; // <-- NEW IMPORT

// On-load animation for the header
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const servicesList = [
  {
    icon: <FaCode />,
    title: 'Custom Web Development',
    description: 'We build bespoke, high-performance web applications from the ground up. Our solutions are secure, scalable, and tailored precisely to your business needs, from complex enterprise platforms to sleek marketing sites.',
    offerings: [
      'Full-Stack MERN & Django Development',
      'Progressive Web Apps (PWAs)',
      'Custom API Development & Integration',
      'E-commerce & Marketplace Platforms',
    ],
  },
  {
    icon: <FaMobileAlt />,
    title: 'Mobile App Development',
    description: 'Engage your users on the go with beautiful and intuitive mobile applications. We develop for both iOS and Android, ensuring a consistent and high-quality experience across all devices, whether native or cross-platform.',
    offerings: [
      'Native iOS (Swift) & Android (Kotlin)',
      'Cross-Platform (React Native)',
      'UI/UX Design for Mobile',
      'App Store Deployment & Maintenance',
    ],
  },
  {
    icon: <FaBrain />,
    title: 'AI & Data Science',
    description: 'Unlock the power of your data. Our AI and machine learning experts can help you build intelligent systems that automate processes, provide predictive insights, and create innovative, data-driven products.',
    offerings: [
      'Machine Learning Model Development',
      'Natural Language Processing (NLP)',
      'Computer Vision & Image Analysis',
      'Data-Driven Business Intelligence Dashboards',
    ],
  },
  {
    icon: <FaCloud />,
    title: 'Cloud & DevOps',
    description: 'Streamline your operations and scale with confidence. We provide comprehensive DevOps and cloud infrastructure services, from CI/CD pipeline implementation to managing scalable, resilient architectures on AWS, Azure, and GCP.',
    offerings: [
      'Cloud Migration & Architecture',
      'CI/CD Pipeline Implementation',
      'Kubernetes & Docker Containerization',
      'Infrastructure as Code (IaC) & Automation',
    ],
  },
  {
    icon: <FaPaintBrush />,
    title: 'UI/UX Design',
    description: 'A great product starts with a great user experience. Our design team conducts deep user research to create wireframes, prototypes, and high-fidelity designs that are not only beautiful but also intuitive and user-centric.',
    offerings: [
      'User Research & Persona Development',
      'Wireframing & Prototyping',
      'High-Fidelity UI Design',
      'Usability Testing & Design Systems',
    ],
  },
  {
    icon: <FaServer />,
    title: 'API & Backend Systems',
    description: 'The backbone of any modern application. We design and build robust, secure, and highly scalable backend systems and RESTful APIs that can power your web, mobile, and third-party applications reliably.',
    offerings: [
      'RESTful API & GraphQL Development',
      'Microservices Architecture',
      'Database Design & Optimization',
      'Real-time Data Processing',
    ],
  },
];

const techStackList = [
  { icon: <FaReact />, name: 'React' },
  { icon: <SiDjango />, name: 'Django' },
  { icon: <FaPython />, name: 'Python' },
  { icon: <FaNodeJs />, name: 'Node.js' },
  { icon: <SiMongodb />, name: 'MongoDB' },
  { icon: <FaAws />, name: 'AWS' },
  { icon: <FaDocker />, name: 'Docker' },
];

const Services = () => {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      exit={{ opacity: 0 }}
      style={{ paddingTop: '100px' }}
    >
      <div className="container">
        {/* 1. Header Section (Uses on-load animation) */}
        <div className="services-header">
          <motion.h1 variants={itemVariants}>What We Do</motion.h1>
          <motion.p variants={itemVariants} style={{ transitionDelay: '0.1s' }}>
            Our solutions are engineered to perfection, covering the full
            spectrum of modern software development.
          </motion.p>
        </div>

        {/* 2. Detailed Services List (Refactored) */}
        <div className="services-detail-list">
          {servicesList.map((service, index) => (
            <AnimatedOnScroll
              className="service-detail-item"
              key={index}
              delay={index * 0.1} // Stagger the animation
            >
              <div className="service-detail-icon">{service.icon}</div>
              <div className="service-detail-content">
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <h4>Key Offerings:</h4>
                <ul className="key-offerings">
                  {service.offerings.map((offer, i) => (
                    <li key={i}>{offer}</li>
                  ))}
                </ul>
              </div>
            </AnimatedOnScroll>
          ))}
        </div>
      </div>

      {/* 3. Tech Stack Section (Refactored) */}
      <section className="tech-stack-section">
        <div className="container">
          <AnimatedOnScroll className="centered-header">
            <h2 className="section-title">Our Technology Stack</h2>
          </AnimatedOnScroll>
          <AnimatedOnScroll delay={0.1}>
            <p className="section-subtitle">
              We use the best tools for the job to build reliable and scalable
              software.
            </p>
          </AnimatedOnScroll>
          <div className="tech-grid">
            {techStackList.map((tech, index) => (
              <AnimatedOnScroll className="tech-card" key={index} delay={index * 0.1}>
                {tech.icon}
                <span>{tech.name}</span>
              </AnimatedOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Call to Action (CTA) Section (Refactored) */}
      <section className="cta-section">
        <AnimatedOnScroll className="container cta-container">
          <h2>Have a Specific Need?</h2>
          <p>Let's discuss your project and find the perfect solution.</p>
          <Link to="/contact" className="btn" data-hover>
            Discuss Your Project
          </Link>
        </AnimatedOnScroll>
      </section>
    </motion.div>
  );
};

export default Services;